import httpService, {
  AccountContextRequestInvalidatedError,
  type EventStreamConnection,
  type HttpError,
  type HttpRequestScope,
} from "./httpService";
import { refreshTokensNow } from "../utils/tokenRefresh";
import { SseFrameParser } from "../utils/sseParser";
import { parseRealtimeEvent } from "../utils/realtimeEvents";
import type {
  RealtimeBusinessEvent,
  RealtimeBusinessEventType,
  RealtimeConnectionState,
  RealtimeEvent,
  RealtimeStreamCloseReason,
} from "../types/realtime";

/**
 * Domain-agnostic client for the gateway's authenticated SSE stream (PWA-2689).
 *
 * Keeps one connection alive for the signed-in session and hands typed business
 * events to subscribers. Knows nothing about notifications or any other domain:
 * new server-pushed event types need a union member in `types/realtime.ts`, a
 * validator in `utils/realtimeEvents.ts`, and a subscriber. The lifecycle here
 * never changes for that.
 *
 * Lifecycle rules (spec Section 3.4.3): heartbeat watchdog at 2x the server
 * interval; `lifetime-reached` / `authentication-expired` reconnect after 250 ms
 * (the token is refreshed first for the latter); everything else reconnects
 * with jittered exponential backoff; two consecutive 401s pause for five
 * minutes; offline waits for `online`; an owner `stop()` never reconnects.
 */

export type RealtimeEventHandler<T extends RealtimeBusinessEventType> = (
  event: Extract<RealtimeBusinessEvent, { type: T }>
) => void;

/** The subset of `window` the client needs for connectivity events (method syntax keeps it bivariant). */
export interface ConnectivityTarget {
  addEventListener(type: "online" | "offline", handler: () => void): void;
  removeEventListener(type: "online" | "offline", handler: () => void): void;
}

export interface RealtimeStreamClientOptions {
  openEventStream?: (
    url: string,
    options: { signal: AbortSignal; requestScope?: HttpRequestScope; connectTimeout?: number }
  ) => Promise<EventStreamConnection>;
  refreshTokens?: () => Promise<boolean>;
  random?: () => number;
  path?: string;
  connectTimeoutMs?: number;
  authPauseMs?: number;
  /** Event target for `online` / `offline`; defaults to `window` when present. */
  connectivity?: ConnectivityTarget | null;
  isOnline?: () => boolean;
}

export const REALTIME_STREAM_PATH = "/v3/events/stream";
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_CAP_MS = 30_000;
const TRANSPORT_LOSS_MIN_DELAY_MS = 5_000;
const SERVER_CYCLE_RECONNECT_MS = 250;
const RETRY_AFTER_MIN_MS = 5_000;
const DEFAULT_HEARTBEAT_MS = 15_000;
const AUTH_FAILURES_BEFORE_PAUSE = 2;

type Handler = (event: RealtimeBusinessEvent) => void;

class OwnerAbort extends Error {}

export class RealtimeStreamClient {
  private readonly openEventStream: NonNullable<RealtimeStreamClientOptions["openEventStream"]>;
  private readonly refreshTokens: () => Promise<boolean>;
  private readonly random: () => number;
  private readonly path: string;
  private readonly connectTimeoutMs: number;
  private readonly authPauseMs: number;
  private readonly connectivity: RealtimeStreamClientOptions["connectivity"];
  private readonly isOnline: () => boolean;

  private stateValue: RealtimeConnectionState = "idle";
  private generation = 0;
  private running = false;
  private backoffAttempt = 0;
  private authFailures = 0;
  private subscribers = new Map<string, Set<Handler>>();
  private readyHandlers = new Set<() => void>();
  private stateHandlers = new Set<(state: RealtimeConnectionState) => void>();

  private connectionAbort: AbortController | null = null;
  private connectionDetach: (() => void) | null = null;
  private watchdogTimer: ReturnType<typeof setTimeout> | null = null;
  private watchdogFired = false;
  private sleepTimer: ReturnType<typeof setTimeout> | null = null;
  private sleepResolve: (() => void) | null = null;
  private onlineResolve: (() => void) | null = null;
  private wentOffline = false;
  private readonly handleOnline = () => {
    this.onlineResolve?.();
  };
  private readonly handleOffline = () => {
    if (!this.running) return;
    this.wentOffline = true;
    this.connectionAbort?.abort();
    this.cancelSleep();
  };

  constructor(options: RealtimeStreamClientOptions = {}) {
    this.openEventStream = options.openEventStream
      ?? ((url, streamOptions) => httpService.openEventStream(url, streamOptions));
    this.refreshTokens = options.refreshTokens ?? refreshTokensNow;
    this.random = options.random ?? Math.random;
    this.path = options.path ?? REALTIME_STREAM_PATH;
    this.connectTimeoutMs = options.connectTimeoutMs ?? 15_000;
    this.authPauseMs = options.authPauseMs ?? 5 * 60_000;
    this.connectivity = options.connectivity === undefined
      ? (typeof window === "undefined" ? null : window)
      : options.connectivity;
    this.isOnline = options.isOnline
      ?? (() => (typeof navigator === "undefined" ? true : navigator.onLine !== false));
  }

  get state(): RealtimeConnectionState {
    return this.stateValue;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.generation += 1;
    this.backoffAttempt = 0;
    this.authFailures = 0;
    this.connectivity?.addEventListener("online", this.handleOnline);
    this.connectivity?.addEventListener("offline", this.handleOffline);
    void this.run(this.generation);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    this.generation += 1;
    this.connectivity?.removeEventListener("online", this.handleOnline);
    this.connectivity?.removeEventListener("offline", this.handleOffline);
    this.clearWatchdog();
    this.cancelSleep();
    this.onlineResolve?.();
    this.connectionAbort?.abort();
    this.setState("idle");
  }

  restart(): void {
    this.stop();
    this.start();
  }

  subscribe<T extends RealtimeBusinessEventType>(type: T, handler: RealtimeEventHandler<T>): () => void {
    const handlers = this.subscribers.get(type) ?? new Set<Handler>();
    handlers.add(handler as Handler);
    this.subscribers.set(type, handlers);
    return () => {
      handlers.delete(handler as Handler);
    };
  }

  /** Fires on every `stream.ready` (first connect and every reconnect). */
  onReady(handler: () => void): () => void {
    this.readyHandlers.add(handler);
    return () => {
      this.readyHandlers.delete(handler);
    };
  }

  onStateChange(handler: (state: RealtimeConnectionState) => void): () => void {
    this.stateHandlers.add(handler);
    return () => {
      this.stateHandlers.delete(handler);
    };
  }

  private setState(next: RealtimeConnectionState): void {
    if (this.stateValue === next) return;
    this.stateValue = next;
    for (const handler of this.stateHandlers) {
      try {
        handler(next);
      } catch {
        // Listener errors must not affect the stream.
      }
    }
  }

  private isCurrent(generation: number): boolean {
    return this.running && generation === this.generation;
  }

  private async run(generation: number): Promise<void> {
    while (this.isCurrent(generation)) {
      if (!this.isOnline() || this.wentOffline) {
        this.wentOffline = false;
        this.setState("offline");
        await this.waitForOnline();
        if (!this.isCurrent(generation)) return;
        this.backoffAttempt = 0;
      }

      this.setState("connecting");
      let outcome: { delayMs: number; state: RealtimeConnectionState } | null;
      try {
        outcome = await this.connectAndRead(generation);
      } catch (error) {
        if (!this.isCurrent(generation)) return;
        outcome = this.outcomeForError(error);
      }
      if (!this.isCurrent(generation)) return;
      if (outcome === null) return;

      this.setState(outcome.state);
      if (outcome.delayMs > 0) {
        await this.sleep(outcome.delayMs);
      }
    }
  }

  /** Resolves with the reconnect plan once the connection ends. Throws for connect failures. */
  private async connectAndRead(generation: number): Promise<{ delayMs: number; state: RealtimeConnectionState } | null> {
    const abort = new AbortController();
    this.connectionAbort = abort;
    this.watchdogFired = false;

    let connection: EventStreamConnection;
    try {
      connection = await this.openEventStream(this.path, {
        signal: abort.signal,
        requestScope: "actor",
        connectTimeout: this.connectTimeoutMs,
      });
    } catch (error) {
      this.connectionAbort = null;
      if (this.wentOffline) return { delayMs: 0, state: "offline" };
      throw error;
    }
    if (!this.isCurrent(generation)) {
      connection.detach();
      return null;
    }
    this.connectionDetach = connection.detach;

    const parser = new SseFrameParser();
    let heartbeatMs = DEFAULT_HEARTBEAT_MS;
    let closeReason: RealtimeStreamCloseReason | null = null;

    try {
      for (;;) {
        const { done, value } = await connection.reader.read();
        if (!this.isCurrent(generation)) return null;
        if (done) break;
        if (!value) continue;
        for (const frame of parser.push(value)) {
          const event = parseRealtimeEvent(frame.event, frame.data);
          if (!event) continue;
          if (event.type === "stream.ready") {
            heartbeatMs = event.payload.heartbeatIntervalMs;
            this.backoffAttempt = 0;
            this.authFailures = 0;
            this.setState("open");
            this.armWatchdog(heartbeatMs);
            this.fireReady();
          } else if (event.type === "stream.heartbeat") {
            this.armWatchdog(heartbeatMs);
          } else if (event.type === "stream.closing") {
            closeReason = event.payload.reason;
          } else {
            this.armWatchdog(heartbeatMs);
            this.dispatch(event);
          }
        }
      }
    } catch (error) {
      if (!this.isCurrent(generation)) return null;
      if (this.wentOffline) return { delayMs: 0, state: "offline" };
      if (this.watchdogFired || (error instanceof Error && error.name === "AbortError")) {
        return { delayMs: this.nextBackoffMs(), state: "backoff" };
      }
      return { delayMs: this.nextBackoffMs(), state: "backoff" };
    } finally {
      this.clearWatchdog();
      this.connectionAbort = null;
      this.connectionDetach = null;
      connection.detach();
    }

    if (this.wentOffline) return { delayMs: 0, state: "offline" };

    if (closeReason === "authentication-expired") {
      try {
        await this.refreshTokens();
      } catch {
        // The connect stage refreshes again on 401; nothing else to do here.
      }
      if (!this.isCurrent(generation)) return null;
      return { delayMs: SERVER_CYCLE_RECONNECT_MS, state: "backoff" };
    }
    if (closeReason === "lifetime-reached") {
      return { delayMs: SERVER_CYCLE_RECONNECT_MS, state: "backoff" };
    }
    if (closeReason === "transport-unavailable" || closeReason === "server-shutdown") {
      return { delayMs: this.nextBackoffMs(TRANSPORT_LOSS_MIN_DELAY_MS), state: "backoff" };
    }
    return { delayMs: this.nextBackoffMs(), state: "backoff" };
  }

  private outcomeForError(error: unknown): { delayMs: number; state: RealtimeConnectionState } | null {
    if (error instanceof OwnerAbort) return null;
    if (error instanceof AccountContextRequestInvalidatedError) {
      // An auth session transition is in progress; the owner restarts us when it settles.
      this.running = false;
      this.setState("idle");
      return null;
    }
    const status = (error as HttpError).status;
    if (status === 401) {
      this.authFailures += 1;
      if (this.authFailures >= AUTH_FAILURES_BEFORE_PAUSE) {
        this.authFailures = AUTH_FAILURES_BEFORE_PAUSE - 1;
        return { delayMs: this.authPauseMs, state: "paused" };
      }
      return { delayMs: this.nextBackoffMs(), state: "backoff" };
    }
    if (status === 429 || status === 503) {
      const retryAfterSeconds = Number((error as HttpError).response?.headers?.get?.("Retry-After"));
      if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
        return { delayMs: Math.max(RETRY_AFTER_MIN_MS, retryAfterSeconds * 1000), state: "backoff" };
      }
      return { delayMs: this.nextBackoffMs(), state: "backoff" };
    }
    return { delayMs: this.nextBackoffMs(), state: "backoff" };
  }

  private nextBackoffMs(minDelayMs = 0): number {
    const base = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** this.backoffAttempt);
    this.backoffAttempt = Math.min(this.backoffAttempt + 1, 10);
    const jitter = 0.75 + this.random() * 0.5;
    // Jitter spreads reconnects; the floor is a promise to recovering infrastructure, so it wins.
    return Math.max(minDelayMs, Math.round(base * jitter));
  }

  private armWatchdog(heartbeatMs: number): void {
    this.clearWatchdog();
    this.watchdogTimer = setTimeout(() => {
      this.watchdogTimer = null;
      this.watchdogFired = true;
      this.connectionAbort?.abort();
    }, heartbeatMs * 2);
  }

  private clearWatchdog(): void {
    if (this.watchdogTimer !== null) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      this.sleepResolve = resolve;
      this.sleepTimer = setTimeout(() => {
        this.sleepTimer = null;
        this.sleepResolve = null;
        resolve();
      }, ms);
    });
  }

  private cancelSleep(): void {
    if (this.sleepTimer !== null) {
      clearTimeout(this.sleepTimer);
      this.sleepTimer = null;
    }
    const resolve = this.sleepResolve;
    this.sleepResolve = null;
    resolve?.();
  }

  private waitForOnline(): Promise<void> {
    if (this.isOnline()) return Promise.resolve();
    return new Promise<void>((resolve) => {
      this.onlineResolve = () => {
        this.onlineResolve = null;
        resolve();
      };
    });
  }

  private fireReady(): void {
    for (const handler of this.readyHandlers) {
      try {
        handler();
      } catch (error) {
        console.warn("Realtime ready handler failed", error instanceof Error ? error.message : error);
      }
    }
  }

  private dispatch(event: RealtimeEvent): void {
    const handlers = this.subscribers.get(event.type);
    if (!handlers) return;
    for (const handler of Array.from(handlers)) {
      try {
        handler(event as RealtimeBusinessEvent);
      } catch (error) {
        // Never log the payload; a subscriber bug must not break delivery to others.
        console.warn(`Realtime handler for ${event.type} failed`, error instanceof Error ? error.message : error);
      }
    }
  }
}
